import React, { useEffect, useRef, useState } from "react";
import InnerLayout from "../../../components/layouts/inner-layout";
import LoadingSpinner from "../../../components/helper-components/loading-spinner";
import { useDispatch } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import {
  salesDeliveryItemsGetAction,
  salesInvoiceViewAction,
  salesOrderItemsGetAction,
} from "../../../redux/actions/sales-action";
import CreateEditViewSalesInv from "../../../components/sales-section/sales-invoice/create-edit-view-sales-Inv";

const CLONE_ITEMS_PER_PAGE = 50;
const CLONE_AUTO_LOAD_ITEM_LIMIT = 100;
const CLONE_SCROLL_LOAD_MORE_THRESHOLD_PX = 150;

const getItemKey = (item) => `${item?.item_id ?? ""}-${item?.sodtt_id ?? ""}`;

const AddSalesInvoice = () => {
  const { itemId, type } = useParams();
  const [searchParams] = useSearchParams();

  const itemIdArray = JSON.parse(decodeURIComponent(itemId));

  //
  const cloneID = searchParams.get("cloneID");
  const transfer=searchParams.has("transfer");
  const isClone = cloneID ? true : false;
  //

  const allitems = itemIdArray.map((item) => {
    const { cmr_id, ...rest } = item;
    return rest;
  });
  const [orderDetail, setOrderDetail] = useState({});
  const [cloneItems, setCloneItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [hasMoreItems, setHasMoreItems] = useState(false);
  const [frameNumber, setFrameNumber] = useState(0);
  const dispatch = useDispatch();
  const itemsRef = useRef([]);
  const isFetchingRef = useRef(false);
  const fetchedFramesRef = useRef(new Set());
  const scrollScheduledRef = useRef(false);

  useEffect(() => {
    if (isClone) {
      setLoading(true);
      getCloneFrame({
        id: cloneID,
        frame_number: 0,
        limit: CLONE_ITEMS_PER_PAGE,
        for_clone: true,
      });
    } else if (type === "delivery") {
      setLoading(true);
      dispatch(salesDeliveryItemsGetAction(allitems, onSuccPur, onErrPur));
    } else if (type === "sales") {
      setLoading(true);
      dispatch(salesOrderItemsGetAction(allitems, onSuccPur, onErrPur));
    }
  }, []);

  // Subsequent clone frames (frame 0 is fetched by the mount effect above).
  useEffect(() => {
    if (!isClone || frameNumber === 0) return;
    setItemsLoading(true);
    getCloneFrame({
      id: cloneID,
      frame_number: frameNumber,
      limit: CLONE_ITEMS_PER_PAGE,
      for_clone: true,
    });
  }, [frameNumber]);

  const getCloneFrame = (params) => {
    if (
      isFetchingRef.current ||
      fetchedFramesRef.current.has(params.frame_number)
    ) {
      return;
    }
    isFetchingRef.current = true;
    fetchedFramesRef.current.add(params.frame_number);
    dispatch(salesInvoiceViewAction(params, onCloneSuccPur, onErrPur));
  };

  const onCloneSuccPur = (data) => {
    // Support both the paginated envelope ({data:{data:[...], next_sequence_exist}})
    // and a plain array under data.data, in case this endpoint doesn't paginate
    // for clone the way it does for view.
    const result = data?.data?.data?.[0] ?? data?.data?.[0];
    setOrderDetail(result);

    const newItems = result?.itemData ?? [];
    const seen = new Set(itemsRef.current.map(getItemKey));
    const uniqueNewItems = newItems.filter((item) => !seen.has(getItemKey(item)));
    itemsRef.current = [...itemsRef.current, ...uniqueNewItems];
    setCloneItems(itemsRef.current);

    const more = !!data?.data?.next_sequence_exist;
    setHasMoreItems(more);
    isFetchingRef.current = false;
    setLoading(false);

    if (more && itemsRef.current.length < CLONE_AUTO_LOAD_ITEM_LIMIT) {
      setFrameNumber((data?.data?.frame_number ?? 0) + 1);
    } else {
      setItemsLoading(false);
    }
  };

  const onSuccPur = (data) => {
    setOrderDetail(data?.data);
    setLoading(false);
  };

  const onErrPur = (data) => {
    isFetchingRef.current = false;
    setItemsLoading(false);
    setLoading(false);
  };

  const loadMoreCloneItems = () => {
    if (!hasMoreItems || isFetchingRef.current) return;
    setFrameNumber((prev) => prev + 1);
  };

  const handleCloneItemsScroll = (e) => {
    if (!isClone || scrollScheduledRef.current) return;
    scrollScheduledRef.current = true;
    const target = e.target;
    requestAnimationFrame(() => {
      scrollScheduledRef.current = false;
      const { scrollTop, scrollHeight, clientHeight } = target;
      if (
        scrollHeight - scrollTop - clientHeight <
        CLONE_SCROLL_LOAD_MORE_THRESHOLD_PX
      ) {
        loadMoreCloneItems();
      }
    });
  };

  const orderData = orderDetail?.orderData || {};
  const vendorDetails = orderDetail?.vendorDetails?.[0] || {};
  const prefix = searchParams.get("type") || "sodtt";
  const isNew = searchParams.has("new");
  const auto = searchParams.has("auto");

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <InnerLayout backBtn={true} title={`${isClone ? "Clone" : "Add"} Sales Invoice`}>
          <CreateEditViewSalesInv
            // <CreateEditViewSalesInvoice
            auto={auto}
            isClone={isClone}
            clickedplus={isNew}
            payto={
              isClone
                ? orderDetail?.orderData?.soit_pay_to_address
                : vendorDetails?.[`${prefix}_pay_to_address`]
            }
            shipto={
              isClone
                ? orderDetail?.orderData?.soit_ship_to_address
                : vendorDetails?.[`${prefix}_ship_to_address`]
            }
            pageType="invoice"
            page="add"
            standAlone={type === "invoice"}
            disabled={type === "delivery"}
            type={type}
            transfer={transfer===true}
            orderDetail={isClone ? cloneItems : orderDetail?.itemData}
            itemData={orderDetail?.itemData?.[0]}
            itemsLoading={isClone ? itemsLoading : false}
            onItemsScroll={handleCloneItemsScroll}
            vendorDetails={
              isClone
                ? [orderDetail?.orderData]
                : [
                    {
                      ...vendorDetails,
                      soit_sub_total: orderData?.sub_total,
                      soit_total_gst: orderData?.total_tax,
                      soit_total_discount: orderData?.total_discount,
                      soit_total_amount: orderData?.total_amount,
                      soit_round_off_value: orderData?.round_off_value,
                      soit_tcs_or_tds_amount: orderData?.tcs_or_tds_amount,
                      soit_tcs_or_tds_percentage_id:
                        orderData?.tcs_or_tds_percentage_id,
                      soit_tcs_or_tds_percentage:
                        orderData?.tcs_or_tds_percentage,
                      soit_tds_tcs_type: orderData?.tcs_or_tds_type,
                      soit_is_invoice_dis_percentage:
                        orderData?.is_invoice_dis_percentage ?? true,
                      soit_invoice_discount: orderData?.invoice_discount ?? 0,
                      soit_invoice_discount_percentage:
                        orderData?.invoice_discount_percentage ?? 0,
                    },
                  ]
            }
          />
        </InnerLayout>
      )}
    </>
  );
};
export default AddSalesInvoice;
